import { BaseCssVariablesStream } from "@xflr6/chatbot";
import { SettingsPatch } from "@xflr6/chatbot-api";
import { nullifyIfBlank } from "@xflr6/utils";

interface ChatSettings {
  showScore: boolean;
  disableKeyboardAnswer: boolean;
  disableAutoAnswer: boolean;
  showName: boolean;
  dryRun: boolean;
}

interface ChatSequenceSettings {
  showScore: boolean;
  allowRandomAccess: boolean;
  disableKeyboardAnswer: boolean;
  disableAutoAnswer: boolean;
  showName: boolean;
  nextComponentMessage: string | null;
  lastComponentMessage: string | null;
  dryRun: boolean;
}

const DEFAULT_CHAT_SETTINGS: ChatSettings = {
  showScore: false,
  disableKeyboardAnswer: false,
  disableAutoAnswer: false,
  showName: false,
  dryRun: false,
};

const DEFAULT_CHAT_SEQUENCE_SETTINGS: ChatSequenceSettings = {
  showScore: false,
  allowRandomAccess: false,
  disableKeyboardAnswer: false,
  disableAutoAnswer: false,
  showName: false,
  nextComponentMessage: null,
  lastComponentMessage: null,
  dryRun: false,
};

/**
 * String values in the query string that start with the prefix `~a`, are
 * treated specially.
 *
 * To handle many languages, we need to be able to serialize and deserialize
 * text that has variable length representation. This isn't a straightforward
 * process when using Base64, it seems.
 *
 * The `~a` prefix tells this function that text following it needs to be
 * decoded specially. The text following then needs to be in ASCII format,
 * which is a fixed length representation and can be safely encoded/decoded
 * to/from Base64. This function takes this text and runs it through a pipeline
 * that converts it to how it would be in Unicode, so that it can be displayed
 * correctly on screen.
 */
function extractSettingsFromQueryString<T>(qs: string, defaultSettings: T): T {
  const queryParams = new URLSearchParams(qs);
  const settingsBase64 = queryParams.get("s") ?? queryParams.get("settings");
  if (settingsBase64 == null) return defaultSettings;

  const textDecoder = new TextDecoder();
  try {
    const settingsJSONString = atob(standardBase64(settingsBase64));
    const settings = JSON.parse(settingsJSONString);
    for (const key in settings) {
      if (typeof settings[key] === "string" && settings[key].startsWith("~a")) {
        settings[key] = textDecoder.decode(
          Buffer.from(settings[key].substring(2), "ascii")
        );
      }
    }
    return Object.assign(defaultSettings as any, settings);
  } catch (e) {
    console.log(e);
    return defaultSettings;
  }
}

function standardBase64(urlSafeBase64: string) {
  let standardBase64 = urlSafeBase64.replace(/-/g, "+").replace(/_/g, "/");
  // Add padding back if necessary (optional depending on implementation)
  while (standardBase64.length % 4) {
    standardBase64 += "=";
  }
  return standardBase64;
}

export const extractChatSettings = (qs: string): ChatSettings =>
  extractSettingsFromQueryString(qs, DEFAULT_CHAT_SETTINGS);

export const extractChatSequenceSettings = (qs: string): ChatSequenceSettings =>
  extractSettingsFromQueryString(qs, DEFAULT_CHAT_SEQUENCE_SETTINGS);

export const MIN_NEXT_PROMPT_DELAY_MS = 1000;

export const extractIdentifierType = (qs: string): string | null => {
  const queryParams = new URLSearchParams(qs);
  return queryParams.get("t") ?? queryParams.get("type") ?? null;
};

export const extractLanguage = (qs: string): string | null => {
  const queryParams = new URLSearchParams(qs);
  return queryParams.get("l") ?? queryParams.get("language") ?? null;
};

export function applyThemeSettings(
  $baseCssVariables: BaseCssVariablesStream,
  settings: SettingsPatch
): void {
  $baseCssVariables.next({
    "--primary": settings.chatPrimaryColor ?? null,
    "--accent": settings.chatAccentColor ?? null,
    "--borderRadius": settings.chatBorderRadius ?? null,
    "--spacing": settings.chatSpacing ?? null,
  });
}

export function getTranslatedName(
  name: string,
  tName: Record<string, string> | null | undefined,
  language: string | null | undefined
): string {
  return language ? nullifyIfBlank(tName?.[language]) ?? name : name;
}
